import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Box, ErrorModal, FiservCardStatusEnum, SuccessModal, Text } from '@common-fe/common-fe';

import { MASKED_CARD_NUMBER } from '@/common/constants';
import ROUTES from '@/common/routes';
import ModalWrapper from '@/components/wrappers/ModalWrapper';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useIsLockCardPurseAvailable } from '@/modules/featureFlags';
import { CardAccessModal } from '@/modules/transaction/Cards/components/CardAccessModal/CardAccessModal';
import useCardAccessModalStore from '@/modules/transaction/Cards/components/CardAccessModal/stores/useCardAccessModal.store.ts';
import { LockUnlockCardModal } from '@/modules/transaction/Cards/components/LockUnlockCardModal';
import { ManageAccountAccessModal } from '@/modules/transaction/Cards/components/ManageAccountAccessModal';
import useGetCard from '@/modules/transaction/Cards/hooks/useGetCard';
import { useChangeCardStatus } from '@/modules/transaction/Cards/queries/useChangeCardStatus.query';
import { useGetCardsQuery } from '@/modules/transaction/Cards/queries/useGetCards.query';
import { useSetCardStatusModal } from '@/modules/transaction/Cards/store/useSetCardStatusModal.store';

import useCardReplaceModalStore from '../../store/useCardReplaceModal.store';
import { CardReplaceModal } from '../CardReplaceModal/CardReplaceModal';
import CloseCardConfirmationModal from '../CloseCardConfirmationModal';

import CardActionButtons from './CardActionButtons';
import CardModalWrapper from './CardModalWrapper';

export const ChangeStatusCardModal: React.FC = () => {
  const history = useHistory();
  const { state: { cardId, visible: visibilityModal }, handleReset } = useSetCardStatusModal();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const isCardFlipDisabled = useIsLockCardPurseAvailable();
  const [closeCardConfirmation, setCloseCardConfirmation] = useState(false);
  const [isCardAccessModalVisible, setCardAccessModalVisible] = useState(false);
  const [isLockUnlockCardModalVisible, setLockUnlockCardModalVisible] = useState(false);
  const [isManageAccountAccessModalVisible, setManageAccountAccessModalVisible] = useState(false);

  const [closeCardSuccess, setCloseCardSuccess] = useState(false);
  const [lockUnlockSuccessMessage, setLockUnlockSuccessMessage] = useState<string | null>(null);
  const { id: employeeId } = useParams<{ id?: string }>();
  const { card, isLoading, refetch } = useGetCard({ employeeId, cardId });
  const { refetch: refetchCardList } = useGetCardsQuery(employeeId);
  const { handleReset: handleResetAccessModal, handleToggleCardLock, handleSetLockedPurses } = useCardAccessModalStore();

  const [hideVisibility, setHideVisibility] = useState(false);
  const visible = useMemo(() => (hideVisibility ? false : visibilityModal), [visibilityModal, hideVisibility]);
  const { state: { modalVisible, cardholderName }, handleChangeVisibility, handleChangeCardHolderName, handleChangeCardHolderID, handleChangeCardID, handleChangeCardStatus, handleChangeMailedDate, handleChangeActivationDate } = useCardReplaceModalStore();

  const handleResetStores = useCallback(() => {
    handleResetAccessModal();
    handleReset();
  }, [handleReset, handleResetAccessModal]);

  const setCardInfo = useCallback(() => {
    handleChangeCardHolderName(card.holder.name);
    handleChangeCardHolderID(card.holder.dependentId);
    handleChangeCardID(card.id);
    handleChangeCardStatus(card.status);
    handleChangeMailedDate(card.mailedDate);
    handleChangeActivationDate(card.activationDate);
  }, [handleChangeCardHolderID, handleChangeCardHolderName, handleChangeCardStatus, handleChangeCardID, handleChangeMailedDate, handleChangeActivationDate, card]);

  const { mutateAsync: changeStatusMutation, isPending: changeStatusMutationLoading } = useChangeCardStatus();
  const loadingButton = useMemo(() => changeStatusMutationLoading || isLoading, [changeStatusMutationLoading, isLoading]);

  const handleOpenCardReplacementModal = useCallback(() => {
    setCardInfo();
    handleResetStores();
    handleChangeVisibility();
  }, [handleChangeVisibility, handleResetStores, setCardInfo]);

  const handleChangeCardAccessModalVisible = useCallback(() => {
    setCardAccessModalVisible((prev) => !prev);
    setHideVisibility?.(true);
  }, [setHideVisibility]);

  const handleOpenLockUnlockCardModal = useCallback(() => {
    setLockUnlockCardModalVisible(true);
    setHideVisibility(true);
  }, []);

  const handleCloseLockUnlockCardModal = useCallback(() => {
    setLockUnlockCardModalVisible(false);
    setHideVisibility(false);
  }, []);

  const handleOpenManageAccountAccessModal = useCallback(() => {
    setManageAccountAccessModalVisible(true);
    setHideVisibility(true);
  }, []);

  const handleCloseManageAccountAccessModal = useCallback(() => {
    setManageAccountAccessModalVisible(false);
    setHideVisibility(false);
  }, []);

  const goToEmployeeCardsSection = useCallback(() => {
    history.push(`${ROUTES.EMPLOYEE_VIEW(employeeId)}?scrollToModule=cards`);
  }, [history, employeeId]);

  const handleManageAccountAccessSuccess = useCallback(() => {
    refetch();
    refetchCardList();
    setHideVisibility(false);
    handleResetStores();
    handleAddPermanentSnackbar({
      text: 'Account access has been updated!',
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [refetch, refetchCardList, handleResetStores, handleAddPermanentSnackbar]);

  const handleLockUnlockCardSuccess = useCallback((nextStatus: 'locked' | 'unlocked') => {
    refetch();
    refetchCardList();
    setLockUnlockSuccessMessage(`Card ends ${card?.last4Number || MASKED_CARD_NUMBER} has been ${nextStatus}!`);
    setHideVisibility(true);
  }, [card?.last4Number, refetch, refetchCardList]);

  const handleCloseLockUnlockSuccessModal = useCallback(() => {
    setLockUnlockSuccessMessage(null);
    setHideVisibility(false);
    handleResetStores();
  }, [handleResetStores]);

  const handleCloseErrorModal = useCallback((value: boolean) => {
    handleAddPermanentSnackbar({
      text: `Card ends ${card?.last4Number || MASKED_CARD_NUMBER} was closed!`,
      closeIcon: true,
    });
    setCloseCardSuccess(value);
    setHideVisibility(false);
    handleResetStores();
    goToEmployeeCardsSection();
  }, [card.last4Number, handleAddPermanentSnackbar, handleResetStores, goToEmployeeCardsSection]);

  const handleOpenCloseModal = useCallback(() => {
    setCloseCardConfirmation(true);
    setHideVisibility(true);
  }, []);

  const handleChangeStatusMutationHandler = useCallback(async (status: FiservCardStatusEnum) => {
    if (card) {
      await changeStatusMutation({ id: cardId, status });
      refetch();
      refetchCardList();
    }
  }, [card, cardId, changeStatusMutation, refetch, refetchCardList]);

  useEffect(() => {
    handleToggleCardLock(card?.status === FiservCardStatusEnum.FROZEN || card?.status === FiservCardStatusEnum.PENDING_FROZEN);
    if (card?.cardPurses) handleSetLockedPurses(card.cardPurses);
  }, [card?.cardPurses, card?.status, handleResetAccessModal, handleToggleCardLock, handleSetLockedPurses]);

  if (!card) {
    return null;
  }

  return (
    <>
      <ModalWrapper visible={visible} onSetVisible={handleResetStores}>
        <Box height={{ min: isCardFlipDisabled ? '94px' : '42px' }} />
        <Box background="module" round="container1Round" align="center" justify="end">
          <CardModalWrapper
            card={card}
            isLoading={isLoading}
          />
          {!isCardFlipDisabled && (
            <Text weight={500} size="small" color="textSecondary" margin={{ bottom: 'xl' }}>Click on card to see the accounts</Text>
          )}
          <Text weight="bold" size="xxlarge" color="textBody" margin={{ bottom: 'l' }}>What Would You Like to Do?</Text>
          <CardActionButtons
            card={card}
            loadingButton={loadingButton}
            onOpenLockModal={handleChangeCardAccessModalVisible}
            onOpenLockUnlockCardModal={handleOpenLockUnlockCardModal}
            onOpenCardReplacementModal={handleOpenCardReplacementModal}
            onOpenCloseConfirmationModal={handleOpenCloseModal}
            onOpenManageAccountAccessModal={handleOpenManageAccountAccessModal}
          />
        </Box>
      </ModalWrapper>
      <CloseCardConfirmationModal
        header="Are You Sure That You Want to Close This Card?"
        helpText="You cannot reopen this card after closing it."
        buttonText="Confirm"
        visible={closeCardConfirmation}
        onSetVisible={() => {
          setCloseCardConfirmation(false);
          setHideVisibility(false);
        }}
        onCancel={() => {
          setCloseCardConfirmation(false);
          setHideVisibility(false);
        }}
        submitting={changeStatusMutationLoading}
        onSubmit={async () => {
          await handleChangeStatusMutationHandler(FiservCardStatusEnum.CLOSED);
          setCloseCardConfirmation(false);
          setCloseCardSuccess(true);
          setHideVisibility(true);
        }}
      />
      <ErrorModal
        visible={closeCardSuccess}
        onSetVisible={handleCloseErrorModal}
        header={`Card Ends ${card?.last4Number || MASKED_CARD_NUMBER} Was Closed!`}
        buttonText="Got It!"
      />
      <SuccessModal
        testId="lock-unlock-card-success-modal"
        visible={!!lockUnlockSuccessMessage}
        onSetVisible={handleCloseLockUnlockSuccessModal}
        header={lockUnlockSuccessMessage || ''}
        buttonText="Got it!"
      />
      <CardReplaceModal
        visible={modalVisible}
        cardholderName={cardholderName}
        onSetVisible={handleChangeVisibility}
        employeeId={employeeId}
      />
      {
        isCardAccessModalVisible && (
          <CardAccessModal
            card={card}
            visible={isCardAccessModalVisible}
            onChangeCardAccessModalVisible={handleChangeCardAccessModalVisible}
            onHideVisibility={setHideVisibility}
            refreshCardList={refetchCardList}
          />
        )
      }
      {
        isLockUnlockCardModalVisible && (
          <LockUnlockCardModal
            card={card}
            visible={isLockUnlockCardModalVisible}
            onCancel={handleCloseLockUnlockCardModal}
            onSuccess={handleLockUnlockCardSuccess}
          />
        )
      }
      {
        isManageAccountAccessModalVisible && (
          <ManageAccountAccessModal
            employeeId={employeeId}
            visible={isManageAccountAccessModalVisible}
            onCancel={handleCloseManageAccountAccessModal}
            onSuccess={handleManageAccountAccessSuccess}
          />
        )
      }
    </>
  );
};
